/**
 * The bulk Assign-suppliers change, as one pure pass over the enquiry's rows.
 *
 * Three modes (Akhil, 2026-09-15 — adding to many lines at once needed its inverse):
 *   add     union the suppliers onto each named line (what the dialog always did);
 *   remove  take the named suppliers off each named line;
 *   clear   empty each named line.
 * Contract-routed lines are never touched — they need no RFQ and name no supplier to ask.
 * Untouched rows come back as the SAME objects, because the caller writes `items` wholesale and
 * an untouched row must be sent exactly as it was read.
 */
export type SupplierChangeMode = 'add' | 'remove' | 'clear'

export function applySupplierChange<T extends { enquiry_row_no: string; route?: string | null; suppliers?: string[] | null }>(
  rows: readonly T[],
  lineIds: readonly string[],
  suppliers: readonly string[],
  mode: SupplierChangeMode,
): T[] {
  const touched = new Set(lineIds)
  const named = new Set(suppliers)
  return rows.map((row) => {
    if (!touched.has(row.enquiry_row_no) || row.route !== 'rfq') return row
    const current = row.suppliers ?? []
    if (mode === 'clear') return { ...row, suppliers: [] }
    if (mode === 'remove') return { ...row, suppliers: current.filter((s) => !named.has(s)) }
    return { ...row, suppliers: [...new Set([...current, ...suppliers])] }
  })
}

/** "1, 2 and 6" — serials in numeric order, so the list reads as the table does. */
const listSerials = (serials: string[]): string => {
  const sorted = [...serials].sort((a, b) => Number(a) - Number(b) || a.localeCompare(b))
  if (sorted.length <= 1) return sorted.join('')
  return `${sorted.slice(0, -1).join(', ')} and ${sorted[sorted.length - 1]}`
}

/**
 * The lines a bulk Assign-suppliers will actually write, NAMED — for the dialog's intro.
 *
 * Akhil, 2026-09-16: a supplier meant for one newly added line went out to every line. The ticks
 * from an earlier bulk assign were still on (the page clears them after a bulk match, and did not
 * after a bulk assign), and "Assign suppliers to 6 selected item(s)" gave no way to notice that
 * without scrolling back to the table. Naming the serials makes a stale tick visible at the
 * moment it matters; naming the ones already on a sent RFQ says what adding a supplier there
 * means (`buildRfqPlan`: a fresh RFQ for that supplier, never a rewrite of the sent one).
 */
export function describeSelectedLines(
  lines: readonly { line_id: string; cust_sno: string; route: string }[],
  selected: readonly string[],
  /** `cust_sno` → RFQ id for lines already on a sent RFQ (`sentRfqByRow`). */
  sentRows: ReadonlyMap<string, string>,
): string {
  const ticked = new Set(selected)
  const written = lines.filter((l) => ticked.has(l.line_id) && l.route === 'rfq')
  if (written.length === 0) return 'No RFQ line is ticked'
  const serials = written.map((l) => l.cust_sno)
  const head = `${written.length === 1 ? 'Line' : 'Lines'} ${listSerials(serials)}`
  const sent = serials.filter((s) => sentRows.has(s))
  if (sent.length === 0) return head
  return `${head} — ${sent.length === 1 ? 'line' : 'lines'} ${listSerials(sent)} ${sent.length === 1 ? 'is' : 'are'} already on a sent RFQ; a supplier added now gets a new RFQ for them`
}
